import React, { useEffect, useState } from 'react'
import { View, Text, StyleSheet, TouchableOpacity, ScrollView, Linking } from 'react-native'
import { useTheme } from '@react-navigation/native'
import {
  TextInput,
  Icon as CustomIcon,
  FormLocation,
  FormDate,
  RecurrenceForm,
  CheckBox,
  Screen,
} from '@components'
import { Theme } from '@theme'
import { EncounterPlace, getCommonStyles } from '@utils'
import { useForm } from 'react-hook-form'
import { TimepickerModal } from '@components'
import dayjs from 'dayjs'
import { buildGoogleCalendarUrl } from './googleCalendarLink'
import {
  EventType,
  useCreateCalendarEvent,
  useCreationStackIntegration,
  useGetCalendarEventById,
  useGetContactById,
  useScreen,
  useUpdateCalendarEvent,
} from '@hooks'
import DatePicker from 'react-native-date-picker'
import { useSafeNavigation, useSafeRoute } from '../../../navigation/web/useSafeNavigation'
import ContactsListEdit from '../components/ContactsListEdit'
import { ContactChip } from '../keepContact/components/ContactChip'
import { useLanguage } from '@context'

type AddEventProps = {
  eventId?: string
  contactId?: string
  onBack?: () => void
  onComplete?: () => void
}

export const AddEvent: React.FC<AddEventProps> = ({
  eventId: propEventId,
  contactId: propContactId,
  onBack,
  onComplete,
}) => {
  const navigation = useSafeNavigation()
  const route = useSafeRoute()
  const { isMobile, isWeb } = useScreen()
  const { currentStack, isVisible } = useCreationStackIntegration()

  const params = route?.params as { eventId?: string; contactId?: string }
  const eventId = propEventId || params?.eventId || currentStack?.data?.eventId
  const contactId = propContactId || params?.contactId || currentStack?.data?.contactId
  const { data: event } = useGetCalendarEventById(eventId || '')
  const { data: contact } = useGetContactById(contactId || '')
  const isEdit = !!event

  const theme = useTheme()
  const styles = getStyles(theme)
  const commonStyles = getCommonStyles(theme)
  const { t } = useLanguage()

  const { mutate: createCalendarEvent } = useCreateCalendarEvent()
  const { mutate: updateCalendarEvent } = useUpdateCalendarEvent()

  const [selectedTab, setSelectedTab] = useState<EventType>(EventType.PRIVATE)
  const [eventName, setEventName] = useState('')
  const [description, setDescription] = useState('')
  const [rrule, setRrule] = useState<string | null>(null)
  const [startDate, setStartDate] = useState(dayjs().toDate())
  const [startTime, setStartTime] = useState(dayjs().add(1, 'hour').format('HH:mm'))
  const [endDate, setEndDate] = useState(dayjs().toDate())
  const [endTime, setEndTime] = useState(dayjs().add(2, 'hour').format('HH:mm'))
  const [allDay, setAllDay] = useState<boolean>(false)
  const [location, setLocation] = useState<EncounterPlace>({
    place: '',
    city: '',
    country: '',
    state: '',
    region: '',
  })
  const [hasReminder, setHasReminder] = useState(false)
  const [invitedPeopleIds, setInvitedPeopleIds] = useState<string[]>([])
  const [isDatepickerVisible, setIsDatepickerVisible] = useState<number>()
  const [isTimepickerVisible, setIsTimepickerVisible] = useState<number>()
  const [isInvitedPeopleModalVisible, setIsInvitedPeopleModalVisible] = useState(false)

  const { control } = useForm<any>()

  const resetForm = () => {
    setEventName('')
    setDescription('')
    setInvitedPeopleIds([])
    setStartDate(dayjs().toDate())
    setStartTime('12:30')
  }

  const handleBack = () => {
    if (onBack) {
      resetForm()
      onBack()
    } else {
      navigation?.goBack()
    }
  }

  // Start/end dayjs values from the current form state (shared by save + the
  // "Add to Google Calendar" link).
  const getEventDates = () => {
    const startDt = allDay
      ? dayjs(startDate).startOf('day')
      : dayjs(startDate)
          .hour(parseInt(startTime.split(':')[0], 10))
          .minute(parseInt(startTime.split(':')[1], 10))
    const endDt = allDay
      ? dayjs(endDate).endOf('day')
      : dayjs(endDate)
          .hour(parseInt(endTime.split(':')[0], 10))
          .minute(parseInt(endTime.split(':')[1], 10))
    return { startDt, endDt }
  }

  // Opens Google Calendar pre-filled with this event so the user adds it to
  // their own agenda in one click (no OAuth needed).
  const openInGoogleCalendar = () => {
    const { startDt, endDt } = getEventDates()
    const url = buildGoogleCalendarUrl({
      startDt,
      endDt,
      allDay,
      text: eventName || t('calendarEvent.createEvent'),
      details: description || '',
      location: location?.place || '',
    })
    Linking.openURL(url)
  }

  const handleCreateEvent = () => {
    const { startDt, endDt } = getEventDates()
    const eventData = {
      title: eventName,
      description,
      startDateTime: startDt.toISOString(),
      endDateTime: endDt.toISOString(),
      allDay,
      rrule: rrule ?? null,
      location,
      type: selectedTab,
      participantAccountIds: invitedPeopleIds,
      hasReminder,
      descriptionAsReminder: hasReminder,
    }

    // Only navigate back / reset the form once the save actually succeeds.
    // Doing it unconditionally showed a false "event created" success and lost
    // the user's input when the request failed (see onError toasts above).
    const onSaved = () => {
      if (onComplete) {
        onComplete()
      } else {
        handleBack()
      }
      resetForm()
    }

    if (isEdit) {
      updateCalendarEvent({ id: event.id, ...eventData }, { onSuccess: onSaved })
    } else {
      createCalendarEvent(eventData, { onSuccess: onSaved })
    }
  }

  useEffect(() => {
    if (event) {
      setSelectedTab(event.type)
      setEventName(event.title)
      setDescription(event.description)
      setStartDate(event.startDateTime ? dayjs(event.startDateTime).toDate() : dayjs().toDate())
      setStartTime(
        event.startDateTime
          ? dayjs(event.startDateTime).format('HH:mm')
          : dayjs().add(1, 'hour').format('HH:mm'),
      )
      setEndDate(event.endDateTime ? dayjs(event.endDateTime).toDate() : dayjs().toDate())
      setEndTime(
        event.endDateTime
          ? dayjs(event.endDateTime).format('HH:mm')
          : dayjs().add(2, 'hour').format('HH:mm'),
      )
      setAllDay(event.allDay ?? false)
      setLocation(event.location ?? { place: '', city: '', country: '', state: '', region: '' })
      setRrule(event.rrule ?? null)
      setHasReminder(event.hasReminder ?? false)
    }
  }, [event])

  useEffect(() => {
    setInvitedPeopleIds(event?.participantAccountIds || (contact ? [contact!.id] : []))
  }, [event, contact])

  return (
    <Screen>
      {/* Header */}
      <View style={[styles.header, isWeb && commonStyles.paddingTop]}>
        {(isMobile || !!event?.id) && !isVisible ? (
          <TouchableOpacity onPress={handleBack}>
            <CustomIcon name="arrowBack" size={25} color="#000000" />
          </TouchableOpacity>
        ) : (
          <View style={styles.placeholder} />
        )}
        <Text style={styles.headerTitle}>
          {isEdit ? t('calendarEvent.editEvent') : t('calendarEvent.createEvent')}
        </Text>
        <View style={styles.placeholder} />
      </View>

      {/* Tabs */}
      <View style={styles.tabContainer}>
        <TouchableOpacity
          style={styles.eventTypeTab}
          onPress={() => setSelectedTab(EventType.COMMON)}
        >
          <View style={styles.eventTypeTabContent}>
            <CustomIcon
              name="user"
              size={theme.iconSize.small}
              color={selectedTab === EventType.COMMON ? theme.colors.primary : theme.colors.greyBlue}
            />
            <Text
              style={[
                styles.eventTypeText,
                selectedTab === EventType.COMMON && styles.activeEventTypeText,
              ]}
            >
              {t('calendarEvent.shared')}
            </Text>
          </View>
          {selectedTab === EventType.COMMON && <View style={styles.activeTabIndicator} />}
        </TouchableOpacity>
        <TouchableOpacity
          style={styles.eventTypeTab}
          onPress={() => setSelectedTab(EventType.PRIVATE)}
        >
          <View style={styles.eventTypeTabContent}>
            <CustomIcon
              name="lock"
              size={theme.iconSize.small}
              color={selectedTab === EventType.PRIVATE ? theme.colors.primary : theme.colors.greyBlue}
            />
            <Text
              style={[
                styles.eventTypeText,
                selectedTab === EventType.PRIVATE && styles.activeEventTypeText,
              ]}
            >
              {t('calendarEvent.personal')}
            </Text>
          </View>
          {selectedTab === EventType.PRIVATE && <View style={styles.activeTabIndicator} />}
        </TouchableOpacity>
      </View>

      {/* `automaticallyAdjustKeyboardInsets` (iOS) adds the keyboard overlap as a
          scroll inset AND scrolls the focused input above the keyboard — so the
          tall Description field and the bottom CTA stay reachable. Android relies
          on the manifest's `adjustResize`, which resizes the window and scrolls
          the focused field into view. No KeyboardAvoidingView: it doesn't scroll
          the focused input into view and double-adjusts on top of adjustResize. */}
      <ScrollView
        style={styles.content}
        contentContainerStyle={commonStyles.paddingBottom}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
        automaticallyAdjustKeyboardInsets
      >
        {/* Event Name */}
        <TextInput
          testID="calendar-event-name-input"
          containerStyle={styles.textInputContainer}
          title={t('calendarEvent.eventName')}
          placeholder={t('calendarEvent.eventNamePlaceholder')}
          value={eventName}
          onChangeText={setEventName}
        />
        {/* Invited People */}
        <TouchableOpacity onPress={() => setIsInvitedPeopleModalVisible(true)}>
          <TextInput
            containerStyle={styles.textInputContainer}
            title={t('calendarEvent.addGuests')}
            iconRight="search"
            placeholder={t('calendarEvent.searchGuests')}
            editable={false}
            pointerEvents="none"
          />
        </TouchableOpacity>
        {invitedPeopleIds.length > 0 && (
          <View style={styles.invitedContainer}>
            {invitedPeopleIds.map((personId, index) => {
              return (
                <ContactChip
                  key={index}
                  contactId={personId}
                  readOnly={!!contactId && personId === contactId}
                  onRemove={() =>
                    setInvitedPeopleIds((prev) => prev.filter((id) => id !== personId))
                  }
                />
              )
            })}
          </View>
        )}
        {isInvitedPeopleModalVisible && (
          <ContactsListEdit
            selectedContactIds={invitedPeopleIds}
            setSelectedContactIds={setInvitedPeopleIds}
            fitParent={!isMobile}
            onClose={() => setIsInvitedPeopleModalVisible(false)}
          />
        )}

        {/* Date */}
        <View style={styles.formContainerWithBottom}>
          <View style={commonStyles.rowBetween}>
            <Text style={styles.blackTextInput}>{t('calendarEvent.eventDate')}</Text>
            <TouchableOpacity
              style={commonStyles.row}
              onPress={() => {
                setAllDay((prev) => {
                  const next = !prev
                  if (next) {
                    setStartTime('00:00')
                    setEndTime('23:59')
                  }
                  return next
                })
              }}
            >
              <CheckBox isChecked={allDay} />
              <Text style={styles.primaryText}>{t('calendarEvent.allDay')}</Text>
            </TouchableOpacity>
          </View>
          <FormDate
            control={control}
            name="startDate"
            dateFormat="dddd DD MMMM YYYY"
            value={dayjs(startDate).format('DD-MM-YYYY')}
            onChange={(value) => {
              if (!isWeb) {
                setStartTime(dayjs(value).format('HH:mm'))
              }
            }}
            hourValue={startTime}
            onDatePressed={() => setIsDatepickerVisible(1)}
            onHourPressed={() => !allDay && setIsTimepickerVisible(1)}
          />
          <FormDate
            control={control}
            name="endDate"
            dateFormat="dddd DD MMMM YYYY"
            value={dayjs(endDate).format('DD-MM-YYYY')}
            onChange={(value) => {
              if (!isWeb) {
                setEndTime(dayjs(value).format('HH:mm'))
              }
            }}
            hourValue={endTime}
            onDatePressed={() => setIsDatepickerVisible(2)}
            onHourPressed={() => !allDay && setIsTimepickerVisible(2)}
          />
        </View>
        <View style={styles.formContainer}>
          <RecurrenceForm
            value={rrule}
            onChange={setRrule}
            dtstart={dayjs(startDate)
              .hour(parseInt(startTime.split(':')[0], 10))
              .minute(parseInt(startTime.split(':')[1], 10))
              .toDate()}
          />
        </View>
        {/* Description */}
        <TextInput
          testID="calendar-description-input"
          containerStyle={styles.textInputContainer}
          title={t('calendarEvent.description')}
          placeholder={t('calendarEvent.descriptionPlaceholder')}
          value={description}
          onChangeText={setDescription}
          isMultiline
        />
        {/* Location */}
        <View style={styles.formContainer}>
          <FormLocation
            testID="calendar-location-input"
            value={location.place}
            onChange={(value) => setLocation(value)}
            placeholder={t('calendarEvent.addLocation')}
            title={t('calendarEvent.locationTitle')}
            iconRight="mapPin"
          />
        </View>
        {/* Alerte : simple interrupteur activé / désactivé (plus de popup). */}
        <View style={styles.alertRow}>
          <View style={styles.alertLabel}>
            <CustomIcon
              name={hasReminder ? 'bell' : 'bellOff'}
              size={theme.iconSize.small}
              color={hasReminder ? theme.colors.primary : theme.colors.greyBlue}
            />
            <Text style={styles.alertText}>{t('calendarEvent.alert')}</Text>
          </View>
          <TouchableOpacity
            activeOpacity={0.8}
            onPress={() => setHasReminder((v) => !v)}
            style={[styles.toggleTrack, hasReminder && styles.toggleTrackOn]}
          >
            <View style={[styles.toggleThumb, hasReminder && styles.toggleThumbOn]} />
          </TouchableOpacity>
        </View>
        {/* Bonus optionnel : ouvre Google Agenda pré-rempli (n'enregistre PAS
            dans l'app — la sauvegarde se fait avec le bouton principal ci-dessous). */}
        <TouchableOpacity
          style={styles.googleButton}
          onPress={openInGoogleCalendar}
          activeOpacity={0.8}
        >
          <CustomIcon name="calendar" size={theme.iconSize.small} color={theme.colors.primary} />
          <Text style={styles.googleButtonText}>{t('calendarEvent.addToGoogle')}</Text>
        </TouchableOpacity>
        {/* Bouton principal — enregistre le rendez-vous DANS l'application (inchangé) */}
        <TouchableOpacity style={styles.createButton} onPress={handleCreateEvent}>
          <Text style={styles.createButtonText}>
            {isEdit
              ? t('calendarEvent.editEvent')
              : invitedPeopleIds.length > 0
                ? t('calendarEvent.inviteAndAdd')
                : t('calendarEvent.addToCalendar')}
          </Text>
        </TouchableOpacity>
      </ScrollView>
      <TimepickerModal
        isVisible={isTimepickerVisible !== undefined}
        setIsVisible={() => setIsTimepickerVisible(undefined)}
        onChange={(time) => {
          if (isTimepickerVisible === 1) {
            setStartTime(time)
          } else {
            setEndTime(time)
          }
        }}
        value={isTimepickerVisible === 1 ? startTime : endTime}
        fitParent={!isMobile}
      />
      <DatePicker
        //@ts-ignore
        fitParent
        modal
        mode="date"
        date={isDatepickerVisible === 1 ? startDate : endDate}
        open={isDatepickerVisible !== undefined}
        onConfirm={(selectedDate) => {
          const offset = selectedDate.getTimezoneOffset() * 60000
          const localDate = new Date(selectedDate.getTime() - offset)

          if (isDatepickerVisible === 1) {
            setStartDate(localDate)
          } else setEndDate(localDate)
          setIsDatepickerVisible(undefined)
        }}
        onCancel={() => setIsDatepickerVisible(undefined)}
      />
    </Screen>
  )
}

const getStyles = (theme: Theme) =>
  StyleSheet.create({
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingHorizontal: 16,
      paddingBottom: theme.spacing.md,
      backgroundColor: 'white',
      borderBottomWidth: 0.5,
      borderBottomColor: '#C7C7CC',
    },
    headerTitle: {
      fontSize: theme.fontSize.large,
      fontWeight: 'bold',
      color: theme.colors.text,
    },
    placeholder: {
      width: 30,
      height: 30,
    },
    tabContainer: {
      flexDirection: 'row',
      paddingHorizontal: 0,
      marginBottom: 0,
      backgroundColor: theme.colors.white,
      borderBottomWidth: 1,
      borderBottomColor: '#E5E5EA',
    },
    eventTypeTab: {
      flex: 1,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      paddingVertical: 16,
      position: 'relative',
    },
    eventTypeTabContent: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: theme.spacing.sm,
    },
    activeTabIndicator: {
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
      height: 2,
      backgroundColor: theme.colors.primary,
    },
    eventTypeText: {
      fontSize: 16,
      fontWeight: '500',
      marginLeft: 8,
      color: theme.colors.black,
    },
    activeEventTypeText: {
      fontWeight: '600',
    },
    content: {
      flex: 1,
      paddingHorizontal: theme.spacing.lg,
      paddingBottom: theme.spacing.lg,
      backgroundColor: theme.colors.background,
    },
    textInputContainer: {
      paddingTop: theme.spacing.lg,
    },
    invitedContainer: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      marginTop: theme.spacing.md,
    },
    invitedPerson: {
      flexDirection: 'row',
      alignItems: 'center',
      borderRadius: 16,
      borderWidth: 1,
      paddingHorizontal: 12,
      paddingVertical: 6,
      marginRight: 8,
      marginBottom: 8,
    },
    avatar: {
      width: 20,
      height: 20,
      borderRadius: 10,
      marginRight: theme.spacing.sm,
    },
    invitedPersonText: {
      fontSize: 14,
      marginRight: 4,
    },
    formContainer: {
      marginTop: theme.spacing.md,
      gap: theme.spacing.md,
    },
    formContainerWithBottom: {
      marginVertical: theme.spacing.md,
      gap: theme.spacing.md,
    },
    googleButton: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: theme.spacing.sm,
      borderWidth: 1.5,
      borderColor: theme.colors.primary,
      backgroundColor: theme.colors.primary + '0A',
      borderRadius: theme.border.jumbo * 2,
      paddingVertical: theme.spacing.md,
      marginTop: 20,
    },
    googleButtonText: {
      fontSize: 15,
      fontWeight: '600',
      color: theme.colors.primary,
    },
    createButton: {
      backgroundColor: theme.colors.primary,
      borderRadius: theme.border.jumbo * 2,
      paddingVertical: theme.spacing.md,
      alignItems: 'center',
      marginTop: 12,
    },
    createButtonText: {
      fontSize: 16,
      fontWeight: '600',
      color: 'white',
    },
    blackTextInput: {
      color: theme.colors.greyBlue,
    },
    primaryText: {
      color: theme.colors.primary,
    },
    alertRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingVertical: theme.spacing.md,
      marginTop: theme.spacing.sm,
    },
    alertLabel: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: theme.spacing.sm,
    },
    alertText: {
      color: theme.colors.secondary,
      fontSize: theme.fontSize.medium,
    },
    toggleTrack: {
      width: 48,
      height: 28,
      borderRadius: 14,
      backgroundColor: theme.colors.grey20 ?? theme.colors.lightGrey,
      paddingHorizontal: 3,
      justifyContent: 'center',
    },
    toggleTrackOn: {
      backgroundColor: theme.colors.primary,
    },
    toggleThumb: {
      width: 22,
      height: 22,
      borderRadius: 11,
      backgroundColor: theme.colors.white,
      alignSelf: 'flex-start',
    },
    toggleThumbOn: {
      alignSelf: 'flex-end',
    },
  })

export default AddEvent
